import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useTranslation } from "../../core/translation.core";
import { useAddCustomer, useUpdateCustomer } from "../../hooks/services/useCustomers";
import { EditingMode, FancyLabelAndTextArea } from "../reuseables/form.comp";
import { FancyLabelAndInput } from "../reuseables/form.comp";
import { createPosCustomerValidationSchema } from "./validations";
import { useRouter } from "next/router";
import { CustomerItem } from "../../types/customer";
import { getPosCustomer } from "../../core/posCustomer.core";
import useNavToNewOrder from "../../hooks/useNavToNewOrder";

export function CustomerEditor({
  user,
  mode,
  handleCloseEditor,
}: {
  user?: CustomerItem;
  mode: EditingMode;
  handleCloseEditor?: VoidFunction;
}) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const methods = useForm({
    resolver: yupResolver(createPosCustomerValidationSchema(t, msg)),
  });

  const editCustomer = useUpdateCustomer();
  const addCustomer = useAddCustomer();
  const navToNewOrder = useNavToNewOrder();

  useEffect(() => {
    if (user) {
      methods.reset({
        email: user.email || undefined,
        firstName: user.firstName || undefined,
        lastName: user.lastName || undefined,
        mobileNumber: user.mobileNumber || undefined,
        remarks: user.remarks || undefined,
      });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, []);

  const onSubmit = async (data) => {
    if (mode === EditingMode.create) {
      try {
        const response = await addCustomer.mutateAsync(data);
        if (response) {
          toast.success(t(msg.customer.customerAdded));

          const customer = await getPosCustomer(response.userId);
          navToNewOrder(customer);
        }
      } catch (error) {
        toast.error(addCustomer.error?.message);
      }
    }

    if (mode === EditingMode.edit) {
      const payload: any = {};

      if (data.email !== user?.email) payload.email = data.email;
      if (data.firstName !== user?.firstName) payload.firstName = data.firstName;
      if (data.lastName !== user?.lastName) payload.lastName = data.lastName;
      if (data.mobileNumber !== user?.mobileNumber) payload.mobileNumber = data.mobileNumber;
      if (data.remarks !== user?.remarks) payload.remarks = data.remarks;

      await editCustomer.mutate(
        {
          countryCode: "852",
          id: user?.id,
          ...payload,
        },
        {
          onError: (err) => {
            console.log(err);
            toast.error(err.message);
          },
          onSuccess: (res) => {
            toast.success(res.message);
            methods.reset(data);
          },
        }
      );

      router.back();
    }
  };

  return (
    <form className="space-y-14 dark:text-white" onSubmit={methods.handleSubmit(onSubmit)}>
      <div className="space-y-5">
        <FancyLabelAndInput type="text" methods={methods} name="lastName" label={t(msg.customer.lastName)} />
        <FancyLabelAndInput type="text" methods={methods} name="firstName" label={t(msg.customer.firstName)} />
        <FancyLabelAndInput type="number" methods={methods} name="mobileNumber" label={t(msg.customer.mobileNumber)} />
        <FancyLabelAndInput type="email" methods={methods} name="email" label={t(msg.customer.email)} />
        <FancyLabelAndTextArea rows={3} name="remarks" methods={methods} label={t(msg.customer.remarks)} />
      </div>

      <div className="flex justify-between gap-4 ">
        {handleCloseEditor ? (
          <button
            onClick={handleCloseEditor}
            type="button"
            className="flex-center gap-3 bg-gray-400 text-white py-2 px-5 w-full rounded-md"
          >
            <span>{t(msg.form.cancel)}</span>
          </button>
        ) : null}

        <button id="btn-save" className="flex-center gap-3 bg-her-blue text-white py-3 px-5 w-full rounded-md">
          <span>{t(msg.form.saveInformation)}</span>
        </button>
      </div>
    </form>
  );
}
